/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  fetchModules,
  updateTopic,
  deleteTopic as _deleteTopic,
} from "../../../api";
import { RootState } from "../../store";
import { Edits, Topic, LoadStatus } from "../../../types";

export interface TopicsState {
  topics: Record<string, Topic>;
  topicEdits: Record<string, Edits<Topic>>;
  topicLoadStatus: LoadStatus;
}

const initialState: TopicsState = {
  topics: {},
  topicEdits: {},
  topicLoadStatus: LoadStatus.NONE,
};

/** Actions */

export const loadTopics = createAsyncThunk(
  "topics/loadTopics",
  async (_, thunkAPI): Promise<Topic[]> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await fetchModules(state.login.accessToken);
  }
);

export const addOrUpdateTopic = createAsyncThunk(
  "topics/addOrUpdateTopic",
  async (data: Partial<Topic>, thunkAPI): Promise<Topic> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await updateTopic(state.login.accessToken, data);
  }
);

export const deleteTopic = createAsyncThunk(
  "topics/deleteTopic",
  async (id: string, thunkAPI): Promise<void> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    await _deleteTopic(state.login.accessToken, id);
  }
);

export const revertTopic = createAsyncThunk(
  "topics/revertTopic",
  async (id: string, thunkAPI): Promise<Topic> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken)
      throw new Error("login required");
    const edits = state.topics.topicEdits[id];
    if (
      !edits ||
      !edits.oldData ||
      `${JSON.stringify(edits.oldData)}` === `${JSON.stringify(edits.newData)}`
    ) {
      throw new Error("no changes to revert");
    }
    return await updateTopic(state.login.accessToken, edits.oldData);
  }
);

export const topicsSlice = createSlice({
  name: "topics",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // load topics
      .addCase(loadTopics.pending, (state) => {
        state.topicLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(loadTopics.fulfilled, (state, action) => {
        for (const topic of action.payload) {
          state.topics[topic.id] = topic;
          state.topicEdits[topic.id] = {
            oldData: topic,
          };
        }
        state.topicLoadStatus = LoadStatus.DONE;
      })
      .addCase(loadTopics.rejected, (state) => {
        state.topicLoadStatus = LoadStatus.FAILED;
      })
      // add or update topic
      .addCase(addOrUpdateTopic.fulfilled, (state, action) => {
        const topic = action.payload;
        if (state.topics[topic.id] && state.topicEdits[topic.id]) {
          state.topicEdits[topic.id].newData = topic;
        } else {
          state.topicEdits[topic.id] = {
            oldData: topic,
            created: true,
          };
        }
        state.topics[topic.id] = topic;
      })
      // delete topic
      .addCase(deleteTopic.fulfilled, (state, action) => {
        const id = action.meta.arg;
        delete state.topics[id];
      })
      // revert topic
      .addCase(revertTopic.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.topics[id] = action.payload;
        state.topicEdits[id].oldData = action.payload;
        state.topicEdits[id].newData = undefined;
      });
  },
});

export default topicsSlice.reducer;
